import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import ConditionalWrapper from '@components/common/conditional-wrapper/conditional-wrapper';
import PersonalizedHeader from '@components/personalized/personalized-header/personalized-header';
import Sort, { SortOptions } from '@components/catalog/sort/sort';
import { useQuery } from '@tanstack/react-query';
import { UserProfile } from '@interfaces/models/userProfile';
import { ProfileQueryKeys } from '@enums/react-query-keys';
import { getUserProfile } from '@api/profile';
import useUser from '@hooks/user/use-user';
import { useRouter } from 'next/router';
import { PageType } from '@interfaces/common/common';
import { capitalize } from '@helpers/utils/general';
import { ElasticSearch } from '@interfaces/models/elasticSearch';

type FavoritesHeaderProps = {
  handleSortClick: (val: string) => void;
  sortBy: ElasticSearch['sortBy'];
  totalCount: number;
  isLoadingProducts: boolean;
  hasHeader?: boolean;
  hasOnlySubheader?: boolean;
  pageType: PageType;
};

const FavoritesHeader: React.FC<FavoritesHeaderProps> = ({
  totalCount,
  sortBy,
  handleSortClick,
  isLoadingProducts,
  hasHeader,
  hasOnlySubheader,
  pageType,
}) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const router = useRouter();
  const { favoritesUserSlug } = router?.query as {
    favoritesUserSlug?: string;
  };

  const FAV_SORT_OPTIONS: SortOptions[] = [
    { value: 'dateDesc', text: t('FAVORITE_PAGE.SORT.RECENTLY_ADDED') },
    { value: 'recentPriceDrop', text: t('FAVORITE_PAGE.SORT.PRICE_DROPS') },
    { value: 'price', text: t('FAVORITE_PAGE.SORT.PRICE_LOW_TO_HIGH') },
    { value: 'priceDesc', text: t('FAVORITE_PAGE.SORT.PRICE_HIGH_TO_LOW') },
  ];
  const [isSortModalOpen, setIsSortModalOpen] = useState<boolean>(false);

  const { data: profileUser } = useQuery<UserProfile | null>({
    queryKey: [ProfileQueryKeys.PROFILE_USER],
    queryFn: () => getUserProfile(favoritesUserSlug?.[0]),
    staleTime: Infinity,
    enabled: favoritesUserSlug?.[0] !== user?.id,
  });

  return (
    <ConditionalWrapper
      condition={hasHeader || hasOnlySubheader}
      wrap={(children) => (
        <PersonalizedHeader
          productsCount={totalCount}
          isLoading={isLoadingProducts}
          title={
            favoritesUserSlug?.[0] !== user?.id && pageType === 'favourite'
              ? profileUser?.firstname || profileUser?.username
                ? t('FAVORITES.TITLE.TEXT', {
                    username: capitalize(profileUser?.firstname || profileUser?.username),
                  })
                : t('FAVORITE_PAGE.TEXT')
              : t('FAVORITE_PAGE.TEXT')
          }
          subTitle={totalCount === 1 ? t('RECOMMENDED_PRODUCT.ITEM_TEXT') : t('FAVORITE_PAGE.ITEMS_TEXT')}
          pageType={pageType}
          hasOnlySubheader={hasOnlySubheader}
        >
          {children}
        </PersonalizedHeader>
      )}
    >
      {(hasHeader || hasOnlySubheader) && (
        <Sort
          defaultValue={sortBy}
          options={FAV_SORT_OPTIONS}
          value={sortBy}
          onChange={(sortedValue) => handleSortClick(sortedValue)}
          open={isSortModalOpen}
          fullScreenOnMobile={true}
          openDialog={setIsSortModalOpen}
        />
      )}
    </ConditionalWrapper>
  );
};

export default FavoritesHeader;
